import type { CashMovement, Shift } from './mock-data'
import { movementLabel, movementNote, openingGap, takenAtClose, takeoutLabel } from './drawer-float'
import { clockText, numDateText } from './i18n'
import type { LateShiftTotals } from './sync-flags'
import type { ExchangeShiftTotals } from './exchange'
import { shownSplit, type ReturnsSplit } from './cash'
import { roundMoney } from './money-round'

/**
 * Printing a thermal document, in its own window.
 *
 * WHY IT IS NOT @media print. Printing the on-screen dialog with print CSS
 * failed five times for five different reasons: the receipt is not a direct
 * child of <body>, so hiding its siblings hid it too; switching to
 * `visibility` left the whole app occupying the page; the dialog library keeps
 * `position: fixed` on wrappers we had not named; and — the one that outlasted
 * three fixes — Tailwind v4 emits `translate` rather than `transform`, so
 * every reset of `transform` reset nothing.
 *
 * All of that was effort spent overriding a surface we do not own. A receipt
 * is a document, not a screenshot of a dialog: built as its own HTML it has no
 * portal, no transform, no dialog CSS and no Tailwind version to track.
 *
 * This module exists because the Z-report was still on the old approach and
 * printed top-left and cut off — the same bug, in the one place that had not
 * been converted. One implementation now, so there is no second place to
 * forget.
 */

/** Escape text that will be dropped into the generated HTML. */
export const esc = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** Two decimals, no currency symbol — the templates add ₾ where they want it. */
// 1.53 — rounded as the database rounds (lib/money-round): 4.725 prints 4.73, as stored
export const m2 = (n: number) => (Number.isFinite(n) ? roundMoney(n).toFixed(2) : String(n))   // a broken figure stays visible

/**
 * The shared stylesheet for 72 mm thermal paper. Prints correctly on 80 mm
 * rolls and simply uses part of the sheet on A4, so one template covers both
 * without asking the shop what it owns.
 */
export const THERMAL_CSS = `
  @page { size: 72mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 4mm 3mm; width: 72mm;
    font-family: "Courier New", ui-monospace, monospace;
    font-size: 11px; line-height: 1.35; color: #000; background: #fff; }
  .c { text-align: center; }
  .shop { font-size: 15px; font-weight: 700; letter-spacing: .5px; }
  .sub { font-size: 10px; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  .meta { display: flex; justify-content: space-between; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 9px; text-transform: uppercase; text-align: right; font-weight: 400; padding-bottom: 2px; }
  th.nm { text-align: left; }
  td { vertical-align: top; padding: 1px 0; }
  td.nm { text-align: left; word-break: break-word; }
  td.q { text-align: right; width: 22px; }
  td.p { text-align: right; width: 42px; }
  td.a { text-align: right; width: 52px; font-weight: 700; }
  .row { display: flex; justify-content: space-between; font-size: 11px; }
  .row.b { font-weight: 700; }
  .total { display: flex; justify-content: space-between; align-items: baseline; font-weight: 700; margin: 4px 0; }
  .total .lbl { font-size: 12px; }
  .total .val { font-size: 20px; }
  .foot { font-size: 10px; }
  @media screen { body { margin: 12px auto; border: 1px solid #ddd; } }
`

/** In a frame on screen the frame IS the paper: no page margin, no outline. */
const FRAMED_CSS = '@media screen { html, body { overflow: hidden } body { margin: 0; border: 0 } }'

/** The whole thermal document — what the print window holds, and what a
 *  frame on screen shows (`framed`, the Z-report dialog), so the two cannot
 *  differ in anything but the frame. */
export function thermalPage(title: string, bodyHtml: string, framed = false): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
    `<style>${THERMAL_CSS}${framed ? FRAMED_CSS : ''}</style></head><body>${bodyHtml}</body></html>`
}

/**
 * Open the document in its own window and send it to the printer.
 * @returns false when the browser blocked the pop-up — the caller must say so.
 */
export function printThermal(title: string, bodyHtml: string): boolean {
  const win = window.open('', '_blank', 'width=420,height=700')
  if (!win) return false
  win.document.write(thermalPage(title, bodyHtml))
  win.document.close()
  win.focus()
  // A beat for layout — otherwise Chrome can open the print dialog on an empty
  // document. Same delay the label printer uses.
  setTimeout(() => win.print(), 400)
  return true
}

/** The shop details every printed document starts with. */
export type PrintableShop = {
  companyName?: string; companyId?: string; address?: string; phone?: string
}

/** The shop's name and the lines under it, as text — the paper and the
 *  customer display (1.56) both draw the header from this.
 *  1.73: no phone unless a slip asks for it. `settings.phone` is also the contact
 *  the platform panel reads, and the owner does not want it on a receipt
 *  (2026-11-05). The repair ticket asks: its customer has to call back. */
export function shopHeader(s: PrintableShop, opts: { phone?: boolean } = {}): { name: string; lines: string[] } {
  return {
    name: s.companyName || '—',
    lines: [s.companyId ? `ს/კ ${s.companyId}` : '', s.address, opts.phone ? s.phone : ''].filter(Boolean).map((l) => String(l)),
  }
}

/** The shop's name and details, as every printed document starts. */
export function thermalHeader(s: PrintableShop, opts: { phone?: boolean } = {}): string {
  return shopHeaderHtml(shopHeader(s, opts))
}

export function shopHeaderHtml(h: { name: string; lines: string[] }): string {
  const lines = h.lines.map((l) => `<div class="sub">${esc(l)}</div>`).join('')
  return `<div class="c"><div class="shop">${esc(h.name)}</div>${lines}</div>`
}

/** The Z-report's title — on the paper's window and the screen's dialog. */
export const Z_TITLE = 'Z-რეპორტი'

/**
 * The Z-report — the paper a cashier hands the owner with the cash.
 *
 * Printed from three places on purpose. The close-shift dialog is the obvious
 * one, but a shop whose printer is not connected at closing time still has to
 * be able to produce it later, and a CASHIER cannot open /staff at all
 * (migration 057) — so the POS offers the last closed shift too. The owner
 * also READS it on screen from the shift history (2026-09-30), printing only
 * if he wants paper.
 */
export function printZReport(report: Shift, shop: PrintableShop, movements: CashMovement[] = [], opts: ZOptions = {}): boolean {
  return printThermal(Z_TITLE, zReportBody(report, shop, movements, opts))
}

export type ZOptions = {
  /** Does the shop use ნისია (`useFeature('credit')`)? Absent = yes, as before. */
  credit?: boolean
  /** 140 — what reached this shift after it closed, by payment rail. The Z's
   *  frozen numbers and variance stay unchanged; a separate appendix explains it. */
  late?: LateShiftTotals
  /** 1.36 — exchanges this shift made (lib/exchange.ts exchangesByShift): named under the returns. */
  exchanges?: ExchangeShiftTotals
  /** 1.39 — this shift's returns by rail (lib/cash.ts returnsSplit), printed
   *  under the returns and, the cash part, among the drawer's lines. Only when
   *  it adds up to the frozen `returns_total` (shownSplit). */
  returns?: ReturnsSplit
}

/**
 * The two ნისია lines of a shift (credit sold, its cash down payments) are
 * for a shop that sells on credit. Where ნისია is switched off they were two
 * ₾0.00 rows to read past (owner, 2026-10-03, SAMGORI's closing screen) —
 * but money is never hidden: a shift that DID carry credit shows both.
 */
export function showCreditLines(creditOn: boolean, creditSales: number, creditPaid: number): boolean {
  return creditOn || Math.abs(creditSales) >= 0.005 || Math.abs(creditPaid) >= 0.005
}

/** The Z-report's paper as HTML — a document, so Georgian on every device. */
export function zReportBody(report: Shift, shop: PrintableShop, movements: CashMovement[] = [], opts: ZOptions = {}): string {
  const credit = showCreditLines(opts.credit ?? true, report.credit_sales, report.credit_paid)
  const when = (iso: string | null) => (iso ? numDateText(iso, { time: true }, 'ka') : '—')
  const line = (label: string, value: string, bold = false) =>
    `<div class="row${bold ? ' b' : ''}"><span>${esc(label)}</span><span>${esc(value)}</span></div>`
  const signed = (value: number) => `${value > 0.005 ? '+' : value < -0.005 ? '−' : ''}₾${m2(Math.abs(value))}`

  // 107 — every movement under its total, oldest first: the paper says who
  // took what and why, not only a sum. The TOTALS come from the shift row
  // (frozen by close_shift); this list is the store's copy of the same rows.
  // A long note is cut to fit a 58mm roll.
  const clock = (iso: string) => clockText('ka', iso)
  const moveLines = [...movements]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((m) => {
      const n = movementNote(m)
      const note = n ? ` — ${n.length > 24 ? `${n.slice(0, 23)}…` : n}` : ''
      return `<div class="meta"><span>${esc(`${clock(m.created_at)} · ${movementLabel(m)}${note}`)}</span>`
        + `<span>${m.direction === 'in' ? '+' : '−'}₾${m2(m.amount)}</span></div>`
    })
    .join('')

  // 176 — the sales a prepaid paid are the third rail; the money taken for one is not turnover
  const ppSales = report.prepaid_sales ?? 0
  const turnover = report.cash_sales + report.card_sales + report.credit_sales + ppSales - report.returns_total
  // 1.39 — where the returns went back; the cash part is what the expected cash took off
  const rs = shownSplit(opts.returns, report.returns_total)
  const sub = (label: string, value: number) =>
    value >= 0.005 ? `<div class="meta"><span>${esc(`· ${label}`)}</span><span>−₾${m2(value)}</span></div>` : ''
  const returnRails = rs
    ? sub('ნაღდით — კასიდან', rs.cash) + sub('ბარათით — ბარათზე', rs.card) + sub('წინასწარი თანხით — ბალანსზე', rs.prepaid)
      + sub('ნისიით — ვალიდან', rs.credit) + sub('გაცვლით — ნივთი ნივთზე', rs.exchange)
    : ''
  // 119 — the opening gap, and what stayed / left at close.
  const gap = openingGap(report)
  const taken = takenAtClose(report)
  const takeNote = report.takeout_note ? ` — ${report.takeout_note.length > 20 ? `${report.takeout_note.slice(0, 19)}…` : report.takeout_note}` : ''

  // 168 — „ხურდის თეთრები": the change rounding close_shift froze on the
  // shift; the net is a term of the expected cash, the parts say why.
  const keptIn = report.change_kept_in ?? 0
  const keptOut = report.change_kept_out ?? 0
  const keptPart = (label: string, value: number) =>
    value >= 0.005 ? `<div class="meta"><span>${esc(`· ${label}`)}</span><span>₾${m2(value)}</span></div>` : ''
  const keptLines = keptIn >= 0.005 || keptOut >= 0.005
    ? line('ხურდის თეთრები', signed(keptIn - keptOut)) + keptPart('დარჩა', keptIn) + keptPart('დავამატეთ', keptOut)
    : ''

  // 176 — gift cards sold and deposits taken (+), handed back (−): the cash two are terms of the
  // expected cash; the card two and the terminal's day total are for the bank statement. A
  // transfer is on no column. The terminal's day is NET (Codex, final review #5): card sales −
  // what went back to a card + cards taken by card − cards handed back by card — printed only
  // when the card returns are known (no returns, or a split that adds up), never a wrong one.
  const ppInCash = report.prepaid_in_cash ?? 0
  const ppInCard = report.prepaid_in_card ?? 0
  const ppOutCash = report.prepaid_out_cash ?? 0
  const ppOutCard = report.prepaid_out_card ?? 0
  const cardBack = report.returns_total < 0.005 ? 0 : rs ? rs.card : null
  const termParts = `· ₾${m2(report.card_sales)} გაყიდვები`
    + (cardBack && cardBack >= 0.005 ? ` − ₾${m2(cardBack)} დაბრუნება` : '')
    + ` + ₾${m2(ppInCard)} წინასწარი`
    + (ppOutCard >= 0.005 ? ` − ₾${m2(ppOutCard)} დაბრუნებული წინასწარი` : '')
  const ppLines = ppInCash >= 0.005 || ppInCard >= 0.005 || ppOutCash >= 0.005 || ppOutCard >= 0.005
    ? `<div class="meta"><span>წინასწარ მიღებული (ბრუნვაში არ შედის)</span></div>`
      + (ppInCash >= 0.005 ? line('· ნაღდი', `+₾${m2(ppInCash)}`) : '')
      + (ppInCard >= 0.005 ? line('· ბარათით', `₾${m2(ppInCard)}`) : '')
      + (ppOutCash >= 0.005 ? line('წინასწარ დაბრუნებული — ნაღდი', `−₾${m2(ppOutCash)}`) : '')
      + (ppOutCard >= 0.005 ? line('წინასწარ დაბრუნებული — ბარათით', `₾${m2(ppOutCard)}`) : '')
      + ((ppInCard >= 0.005 || ppOutCard >= 0.005) && cardBack !== null
        ? line('ტერმინალის დღის ჯამი', `₾${m2(report.card_sales - cardBack + ppInCard - ppOutCard)}`)
          + `<div class="meta"><span>${esc(termParts)}</span></div>`
        : '')
    : ''

  const body = `
  ${thermalHeader(shop)}
  <hr>
  <div class="c"><b>Z-რეპორტი · ცვლის დახურვა</b></div>
  <div class="meta"><span>ცვლა №${esc(report.id.slice(0, 8).toUpperCase())}</span></div>
  <div class="meta"><span>მოლარე: ${esc(report.cashier_name ?? '—')}</span></div>
  <div class="meta"><span>გახსნა: ${esc(when(report.opened_at))}</span></div>
  <div class="meta"><span>დახურვა: ${esc(when(report.closed_at))}</span></div>
  <hr>
  ${line('საწყისი კასა', `₾${m2(report.opening_cash)}`)}
  ${gap ? line(`გახსნისას სხვაობა (დატოვა ₾${m2(report.opening_carried ?? 0)})`, `${gap > 0 ? '+' : '−'}₾${m2(Math.abs(gap))}`) : ''}
  ${line('ნაღდი გაყიდვები', `₾${m2(report.cash_sales)}`)}
  ${line('ბარათით', `₾${m2(report.card_sales)}`)}${ppSales >= 0.005 ? line('წინასწარი თანხიდან', `₾${m2(ppSales)}`) : ''}
  ${credit ? line('ნისიით (სულ)', `₾${m2(report.credit_sales)}`) : ''}
  ${credit ? line('ნისიის გადახდილი', `₾${m2(report.credit_paid)}`) : ''}
  ${report.returns_total > 0 ? line('დაბრუნებები', `−₾${m2(report.returns_total)}`) : ''}
  ${returnRails}
  ${opts.exchanges && opts.exchanges.count > 0 ? line(`მათ შორის გაცვლა (${opts.exchanges.count})`, `დამატებით ₾${m2(opts.exchanges.extra)}`) : ''}
  <hr>
  <div class="total"><span class="lbl">ბრუნვა</span><span class="val">₾${m2(turnover)}</span></div>
  ${line('ჩეკების რაოდენობა', String(report.sales_count))}
  <hr>
  ${rs && rs.cash >= 0.005 ? line('დაბრუნება ნაღდით', `−₾${m2(rs.cash)}`) : ''}
  ${rs && rs.exchangeCash >= 0.005 ? line('გაცვლით დაბრუნებული', `−₾${m2(rs.exchangeCash)}`) : ''}
  ${report.customer_payments_cash > 0 ? line('ვალის გადახდები (ნაღდი)', `+₾${m2(report.customer_payments_cash)}`) : ''}
  ${report.supplier_payments_cash > 0 ? line('მომწოდებელს (ნაღდი)', `−₾${m2(report.supplier_payments_cash)}`) : ''}
  ${report.cash_in_total > 0 ? line('შეტანა სალაროში', `+₾${m2(report.cash_in_total)}`) : ''}
  ${report.cash_out_total > 0 ? line('გატანა სალაროდან', `−₾${m2(report.cash_out_total)}`) : ''}
  ${moveLines}${ppLines}
  ${keptLines}
  ${line('მოსალოდნელი კასა', `₾${m2(report.expected_cash)}`, true)}
  ${line('დათვლილი კასა', `₾${m2(report.closing_cash ?? 0)}`, true)}
  ${line('სხვაობა', `${report.difference > 0 ? '+' : report.difference < 0 ? '−' : ''}₾${m2(Math.abs(report.difference))}`, true)}
  ${Math.abs(report.difference) >= 0.005
      ? `<div class="c foot">${report.difference > 0 ? 'ზედმეტობა' : 'დანაკლისი'}</div>` : ''}
  ${report.closing_float != null ? `<hr>${(report.closing_salary ?? 0) > 0 ? line(`ხელფასი — ${report.cashier_name || '—'}`, `−₾${m2(report.closing_salary ?? 0)}`) : ''}${line('კასაში დარჩა', `₾${m2(report.closing_float)}`, true)}` : ''}
  ${taken > 0 ? line(`გატანილი — ${takeoutLabel(report.takeout_reason, report.takeout_bank)}${takeNote}`, `−₾${m2(taken)}`) : ''}
  ${opts.late && opts.late.count > 0 ? `
  <hr>
  <div class="c"><b>დახურვის შემდეგ მიღებული</b></div>
  ${line('ნაღდი', signed(opts.late.cash))}
  ${line('ბარათი', signed(opts.late.card))}
  ${line('ნისია', signed(opts.late.credit))}
  ${line(`სულ (${opts.late.count})`, signed(opts.late.total), true)}
  <div class="c foot">ამ ცვლის თანხებსა და სხვაობაში არ შედის</div>` : ''}
  <hr>
  <div class="c foot">ხელმოწერა ____________________</div>`

  return body
}
